// A `useSyncExternalStore` source per media query. Exported as a store factory
// rather than a `useMediaQuery(query)` hook on purpose: a generic hook invites
// JS-driven styling that belongs in `@media` blocks. Callers build one named,
// purpose-built hook each and keep the query at module scope.

interface MediaQueryStore {
	subscribe: (on_change: () => void) => () => void;
	get_snapshot: () => boolean;
}

// One store per query at module scope: `useSyncExternalStore` resubscribes
// when `subscribe` changes identity, so a closure re-registers every render.
const media_query_store = (
	query: string,
	// Answer when the platform has no `matchMedia` — SSR, old clients, jsdom
	// without a stub. Each caller picks the safe assumption for its own query.
	fallback: boolean,
): MediaQueryStore => {
	const subscribe = (on_change: () => void): (() => void) => {
		if (!window.matchMedia) {
			return () => {};
		}
		const media_query_list = window.matchMedia(query);
		media_query_list.addEventListener('change', on_change);
		return () => media_query_list.removeEventListener('change', on_change);
	};

	const get_snapshot = (): boolean => {
		if (!window.matchMedia) {
			return fallback;
		}
		return window.matchMedia(query).matches;
	};

	return { subscribe, get_snapshot };
};

export type { MediaQueryStore };
export { media_query_store };
